import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Lock, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { subscriptionService } from '../services/api';

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const { user, logout, hasActiveSubscription } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState('annual');

  // If access is granted (e.g. free period), send the user on to the dashboard
  React.useEffect(() => {
    if (hasActiveSubscription()) navigate('/dashboard', { replace: true });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const response = await subscriptionService.checkout(plan);
      
      const checkoutUrl = 
        response.data?.sessionUrl || 
        response.data?.checkout_url || 
        response.data?.redirect_url ||
        response.data?.url;
      
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error starting checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Lock sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Subscription Required
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your account doesn't have an active subscription.
            Subscribe to access the SalonLogic Pricing Calculator.
          </Typography>
        </Box>
        
        {/* User Info */}
        {user && (
          <Box sx={{ 
            bgcolor: '#f5f5f5', 
            p: 2, 
            borderRadius: 2, 
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Logged in as:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {user.email}
              </Typography>
            </Box>
            <Button variant="text" color="inherit" onClick={logout}>
              Logout
            </Button>
          </Box>
        )}
        
        {/* Plan Selection */}
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Choose Your Plan
        </Typography>
        
        <Grid container spacing={3} justifyContent="center">
          {/* Annual Plan */}
          <Grid item xs={12} sm={6}>
            <Card 
              variant="outlined"
              onClick={() => setSelectedPlan('annual')}
              sx={{ 
                cursor: 'pointer',
                borderWidth: 2,
                borderColor: selectedPlan === 'annual' ? 'primary.main' : 'divider',
                height: '100%'
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6">Annual Plan</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', my: 2 }}>
                  £97
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  per year
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'left' }}>
                  {['Full calculator access', 'PDF & Excel exports', 'All updates included'].map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="success" sx={{ fontSize: 18, mr: 1 }} />
                      <Typography variant="body2">{f}</Typography>
                    </Box>
                  ))}
                </Box>
                
                <Button
                  variant={selectedPlan === 'annual' ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => handleSubscribe('annual')}
                  disabled={loading}
                >
                  {loading && selectedPlan === 'annual' ? 'Processing...' : 'Subscribe - £97/year'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Lifetime Plan */}
          <Grid item xs={12} sm={6}>
            <Card 
              variant="outlined"
              onClick={() => setSelectedPlan('lifetime')}
              sx={{ 
                cursor: 'pointer',
                borderWidth: 2,
                borderColor: selectedPlan === 'lifetime' ? 'success.main' : 'divider',
                height: '100%',
                position: 'relative'
              }}
            >
              <Box sx={{ 
                position: 'absolute', 
                top: -12, 
                left: '50%', 
                transform: 'translateX(-50%)',
                bgcolor: 'success.main',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                BEST VALUE
              </Box>
              
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6">Lifetime Access</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main', my: 2 }}>
                  £199.99
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  one-time payment
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'left' }}>
                  {['Everything in Annual', 'Priority support', 'Never pay again'].map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="success" sx={{ fontSize: 18, mr: 1 }} />
                      <Typography variant="body2" fontWeight={i === 2 ? 'bold' : 'normal'}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
                
                <Button
                  variant={selectedPlan === 'lifetime' ? 'contained' : 'outlined'}
                  color="success"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => handleSubscribe('lifetime')}
                  disabled={loading}
                >
                  {loading && selectedPlan === 'lifetime' ? 'Processing...' : 'Get Lifetime - £199.99'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            🔒 Secure payment powered by Stripe
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questions? Email{' '}
            <a href="mailto:andrew@salonlogic.co.uk">andrew@salonlogic.co.uk</a>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubscriptionRequired;